import { Children, cloneElement, isValidElement, type ReactNode } from "react";
import { Label } from "@/components/ui/label";

// Common wrapper for the field renderers. Every input shares the same
// chrome: label (with optional required marker), the input itself (passed
// as children), help text, and an inline error message. Centralising this
// lets each *Field component focus on the kind-specific input wiring.
//
// Accessibility: the help + error text get stable ids and are linked to the
// input via aria-describedby (injected onto the single child element so the
// renderers don't each have to wire it). Screen readers then announce the
// help/error text when the input is focused — important on the anonymous
// public embed form.

export function FieldShell({
  id,
  label,
  required,
  helpText,
  error,
  children,
}: {
  id: string;
  label: string;
  required?: boolean;
  helpText?: string;
  error?: string;
  children: ReactNode;
}) {
  const helpId = helpText ? `${id}-help` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [errorId, helpId].filter(Boolean).join(" ") || undefined;

  const child = Children.only(children);
  const childProps = isValidElement(child)
    ? (child.props as { "aria-describedby"?: string })
    : null;
  const describedChild =
    describedBy && isValidElement(child) && childProps?.["aria-describedby"] == null
      ? cloneElement(
          child as React.ReactElement<{ "aria-describedby"?: string }>,
          { "aria-describedby": describedBy },
        )
      : child;

  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>
        {label}
        {required ? <span className="ml-0.5 text-destructive">*</span> : null}
      </Label>
      {describedChild}
      {helpText ? (
        <p id={helpId} className="text-[0.8rem] text-muted-foreground">
          {helpText}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} className="text-[0.8rem] font-medium text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}

export type FieldComponentProps = {
  field: import("@colab/shared").FormField;
  control: import("react-hook-form").Control<Record<string, unknown>>;
  disabled?: boolean;
  /**
   * True for the (future) public embed context. PartnerLookupField reads
   * this; the partner portal form-detail page never sets it.
   */
  embedContext?: boolean;
  /** Form slug + draft submission id, passed through to FileField for the
   *  three-step upload. Other field renderers ignore these. */
  slug?: string;
  submissionId?: string | null;
};
